import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Loader } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import RichEditor from "@/components/letters/RichEditor";
import { lcall, letterApi } from "@/lib/letters";
import { Plus, Pencil, Copy, Trash2 } from "lucide-react";

function TemplateDialog({ t, types, onClose, onSaved }) {
  const [f, setF] = useState(t);
  const save = async () => {
    const body = { nom: f.nom, type: f.type, objet: f.objet, contenu_html: f.contenu_html, actif: f.actif !== false };
    if (await lcall(() => (f.id ? api.put(`/letters/templates/${f.id}`, body) : api.post("/letters/templates", body)), "Modèle enregistré")) { onSaved(); onClose(); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[94vh] overflow-y-auto" data-testid="template-edit-dialog">
        <DialogHeader><DialogTitle className="font-head">{f.id ? "Modifier le modèle" : "Nouveau modèle"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-3 gap-2">
          <label className="text-xs">Nom<Input value={f.nom} onChange={(e) => setF({ ...f, nom: e.target.value })} data-testid="tpl-nom" /></label>
          <label className="text-xs">Type<select value={f.type || ""} onChange={(e) => setF({ ...f, type: e.target.value })} className="w-full h-9 rounded-md border px-2 text-sm" data-testid="tpl-type">
            <option value="">—</option>{types.map((x) => <option key={x}>{x}</option>)}</select></label>
          <label className="text-xs">Objet par défaut<Input value={f.objet || ""} onChange={(e) => setF({ ...f, objet: e.target.value })} data-testid="tpl-objet" /></label>
        </div>
        <RichEditor value={f.contenu_html} onChange={(h) => setF((x) => ({ ...x, contenu_html: h }))} />
        <DialogFooter><Button variant="outline" onClick={onClose}>Annuler</Button><Button className="bg-brand-600 text-white" onClick={save} disabled={!f.nom?.trim()} data-testid="tpl-save">Enregistrer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function LetterTemplates() {
  const [items, setItems] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/letters/templates/list").then((r) => setItems(r.data));
  useEffect(() => { load(); letterApi.config().then(setCfg); }, []);
  if (!items || !cfg) return <Loader />;
  const can = cfg.perms.includes("templates");
  const toggle = async (t) => { if (await lcall(() => api.put(`/letters/templates/${t.id}`, { ...t, actif: !t.actif }), t.actif ? "Modèle désactivé" : "Modèle activé")) load(); };
  const dup = async (t) => { if (await lcall(() => api.post(`/letters/templates/${t.id}/duplicate`), "Modèle dupliqué")) load(); };
  const del = async (t) => { if (window.confirm(`Supprimer le modèle « ${t.nom} » ?`) && await lcall(() => api.delete(`/letters/templates/${t.id}`), "Modèle supprimé")) load(); };
  return (
    <div data-testid="letter-templates">
      <PageHeader title="Modèles de courriers" subtitle="Textes types avec variables dynamiques ({{DESTINATAIRE}}, {{DATE}}…) remplacées automatiquement">
        {can && <Button className="bg-brand-600 hover:bg-brand-700 text-white" onClick={() => setEdit({ nom: "", type: "Lettre", objet: "", contenu_html: "<p>Madame, Monsieur,</p><p></p>", actif: true })} data-testid="tpl-new"><Plus className="h-4 w-4 mr-1.5" />Nouveau modèle</Button>}
      </PageHeader>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {items.map((t) => (
          <div key={t.id} className={`rounded-xl border bg-white p-4 ${t.actif ? "" : "opacity-60"}`} data-testid={`tpl-card-${t.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div><p className="font-semibold">{t.nom}</p><p className="text-xs text-slate-500">{t.type} · {t.objet}</p></div>
              {can && <Switch checked={!!t.actif} onCheckedChange={() => toggle(t)} data-testid={`tpl-toggle-${t.id}`} />}
            </div>
            <div className="text-xs text-slate-600 mt-2 line-clamp-3" dangerouslySetInnerHTML={{ __html: (t.contenu_html || "").replace(/<[^>]+>/g, " ") }} />
            {can && <div className="flex gap-1 mt-3">
              <Button size="sm" variant="outline" onClick={() => setEdit(t)} data-testid={`tpl-edit-${t.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />Modifier</Button>
              <Button size="sm" variant="outline" onClick={() => dup(t)} data-testid={`tpl-dup-${t.id}`}><Copy className="h-3.5 w-3.5 mr-1" />Dupliquer</Button>
              <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => del(t)} data-testid={`tpl-del-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>}
          </div>
        ))}
      </div>
      {edit && <TemplateDialog t={edit} types={cfg.types.map((x) => x.nom)} onClose={() => setEdit(null)} onSaved={load} />}
    </div>
  );
}
